import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Tags, Loader2, RefreshCcw, Save, BookOpen, ShoppingBag } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Generic list-settings panel (used for knowsAbout + hasOfferCatalog).
function SchemaListPanel({ title, description, icon: Icon, endpoint, placeholder, testId }) {
  const [value, setValue] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}${endpoint}`, { headers: getAuthHeader() });
      setValue(res.data.value || "");
      setDefaultValue(res.data.default || "");
    } catch (e) {
      toast.error(`Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}${endpoint}`, { value }, { headers: getAuthHeader() });
      toast.success(`${title} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const reset = () => {
    if (!window.confirm(`Replace ${title} with the default list?`)) return;
    setValue(defaultValue);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lineCount = value.split("\n").filter((l) => l.trim()).length;
  if (loading) {
    return (
      <Card><CardContent className="py-10 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal" /></CardContent></Card>
    );
  }
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="w-5 h-5 text-teal" />
          {title}
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-slate-700">
            Entries <span className="text-slate-400">({lineCount} lines)</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reset}>
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Reset to Defaults
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save
            </Button>
          </div>
        </div>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={16}
          className="font-mono text-xs"
          placeholder={placeholder}
        />
      </CardContent>
    </Card>
  );
}

export default function AdminKeywordTemplates() {
  const [templates, setTemplates] = useState("");
  const [defaultTemplates, setDefaultTemplates] = useState("");
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/seo-keyword-templates`, { headers: getAuthHeader() });
      setTemplates(res.data.templates || "");
      setDefaultTemplates(res.data.default_templates || "");
      setPlaceholders(res.data.placeholders || []);
    } catch (e) {
      toast.error("Failed to load keyword templates");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/seo-keyword-templates`, { templates }, { headers: getAuthHeader() });
      toast.success("Keyword templates saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm("Replace current templates with the default ones?")) return;
    setTemplates(defaultTemplates);
  };

  useEffect(() => {
    load();
  }, []);

  const lineCount = templates.split("\n").filter((l) => l.trim()).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  return (
    <div data-testid="admin-keyword-templates" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Tags className="w-5 h-5 text-teal" />
            SEO Keyword Templates
          </CardTitle>
          <p className="text-sm text-slate-600 mt-1">
            Master keyword list applied to every location via <b>Locations → Edit → SEO → Apply Keyword Templates</b>. One keyword per line.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-3 p-3 bg-teal/5 border border-teal/30 rounded-md text-sm">
            <p className="font-medium text-navy mb-1">Available placeholders:</p>
            <div className="flex flex-wrap gap-2">
              {placeholders.map((p) => (
                <code key={p} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-teal">{p}</code>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Each placeholder is replaced with the location's real value when you click <b>Apply Keyword Templates</b>. Lines with unresolved placeholders (e.g. missing cross streets) are skipped automatically.
            </p>
          </div>

          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">
              Templates <span className="text-slate-400">({lineCount} lines)</span>
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={resetToDefaults} data-testid="reset-defaults-btn">
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Reset to Defaults
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-templates-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>

          <Textarea
            value={templates}
            onChange={(e) => setTemplates(e.target.value)}
            rows={22}
            className="font-mono text-xs"
            placeholder="prepaid phone plans {city} {state}&#10;phone store {city} {state}&#10;..."
            data-testid="templates-textarea"
          />

          <p className="text-xs text-slate-400 mt-2">
            Tip: Apply these to a single location from the Location editor, or use the bulk endpoint to apply to all locations at once.
          </p>
        </CardContent>
      </Card>

      <SchemaListPanel
        title='Schema "knowsAbout" List'
        description='Topics the LocalBusiness knows about — rendered into the JSON-LD schema whenever you click Load Default LocalBusiness Schema. One topic per line.'
        icon={BookOpen}
        endpoint="/admin/schema-knows-about"
        placeholder="Total Wireless&#10;Verizon Value&#10;Master Agent&#10;..."
        testId="admin-schema-knows-about"
      />

      <SchemaListPanel
        title='Schema "hasOfferCatalog" List'
        description='Products and services offered — rendered as an OfferCatalog in the JSON-LD schema. One offer per line.'
        icon={ShoppingBag}
        endpoint="/admin/schema-has-offer-catalog"
        placeholder="Apple iPhone&#10;Samsung Galaxy&#10;Dealer Program&#10;..."
        testId="admin-schema-has-offer-catalog"
      />
    </div>
  );
}
